import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * In-memory countdown timer, e.g. for a resend-email cooldown. `start(60)`
 * begins counting down from 60, decrementing once per second down to 0.
 * Calling `start` again resets/restarts it. Timer is cleared on unmount and
 * whenever a new countdown starts, so no stray intervals survive.
 */
export function useCountdown(): [number, (seconds: number) => void] {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback((seconds: number) => {
    clear();
    setSecondsLeft(seconds);
    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clear();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return [secondsLeft, start];
}
